import React, { useState, useEffect, useMemo } from "react";
import { Button, FormFieldSet, PageTitle, TableAdv, LogoLoader, Backdrop, showToast, Modal } from "../../../components";
import { formatNumber, mapTableRows } from "../../../utils";
import dayjs from "dayjs";
import { fetchInvoicesAgingReport } from "../Invoices/Invoices/InvoiceDashboard";
import { fetchAgingReport } from "./FundingDashboard";
import { usePagination } from "../../../hooks";
import { useDispatch } from "react-redux";
import { getCurrentInvoiceStart } from "../../../redux/slices/invoices/invoicesSlice";
import InvoiceDetails from "../Invoices/Invoices/InvoiceDetails";
const Aging = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [showDetailsId, setShowDetailsId] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedAgingBucket, setSelectedAgingBucket] = useState("all");
  const dispatch = useDispatch();
  const [clientInvoices, setClientInvoices] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [cachedInvoicesMap, setCachedInvoicesMap] = useState({});
  const [selectedDateRange, setSelectedDateRange] = useState({from: null, to: null, });
  const [agingData, setAgingData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [reportAsOfDate, setReportAsOfDate] = useState(null);
  const [fundingFilter, setFundingFilter] = useState("");
  const { page, limit, setPage, handleLimitChange } = usePagination(10);

  const onDateRangeChange = (date) => {
    setSelectedDateRange(date);
    setPage(1); 
  };

  const onFundingFilterChange = (val) => {
    setFundingFilter(val);
    setPage(1);
  };

  const onClickShowDetails = async (id, row, bucket = "all") => {
    setShowDetailsId(id);
    setShowDetails(true);
    setSelectedAgingBucket(bucket);
    
    // Quick load if already cached
    if (cachedInvoicesMap[id]) {
      setClientInvoices(cachedInvoicesMap[id]);
    } else {
      setClientInvoices([]);
    }
  };

  const closeShowDetails = () => {
    setShowDetailsId(false);
    setShowDetails(false);
    setClientInvoices([]);
    setSelectedAgingBucket("all");
  };

  const handleClickForInvoice = (invoiceNum, uiid) => {
    if (!invoiceNum || invoiceNum === "-") {
      return showToast("Cannot preview invoice: Missing invoice number.", "error", 3000);
    }
    
    // Safely determine which parameter to filter API results on
    const isRealUiid = uiid && !String(uiid).startsWith("inv-") && !String(uiid).startsWith("pdf-inv-");
    const filterKey = isRealUiid ? "uiid" : "invoiceNum";
    const filterVal = isRealUiid ? uiid : invoiceNum;

    dispatch(
      getCurrentInvoiceStart({
        filters: { [filterKey]: { type: "NONE", value: filterVal } },
        coll: "Invoice",
        ref: "Client Employee Placement Timesheet",
        incl: ["", "firstName lastName", "", "attachments"],
      }),
    );
    setSelectedInvoice(invoiceNum); 
  };

  const toNumber = (value) => {
    if (!value || value === "-") return 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (typeof value === "string") {
      const parsed = Number(value.replace(/[$,]/g, ""));
      return Number.isFinite(parsed) ? parsed : 0;
    }
    const coerced = Number(value);
    return Number.isFinite(coerced) ? coerced : 0;
  };

  const fromDay = selectedDateRange?.from
    ? dayjs(selectedDateRange.from).startOf("day")
    : null;
  const toDay = selectedDateRange?.to
    ? dayjs(selectedDateRange.to).endOf("day")
    : null;

  const normalizedFrom =
    fromDay && toDay && fromDay.isAfter(toDay) ? toDay : fromDay;
  const normalizedTo =
    fromDay && toDay && fromDay.isAfter(toDay) ? fromDay : toDay;

  const extractNetTerms = (inv) => {
    const terms =
      inv?.netTerms ||
      inv?.clientId?.generalInfo?.placementDetails?.recurring?.netTerms ||
      inv?.clientId?.generalInfo?.placementDetails?.permanent?.netTerms ||
      inv?.clientId?.generalInfo?.netTerms;
    const match = String(terms || "").match(/\d+/);
    return match ? parseInt(match[0], 10) : 30;
  };
  // Normalize client names to enable fuzzy matching between DB and PDF sources
  // "Photon Infotech, Inc. (PHOTON INFOT)" → "photon infotech"
  // "Photon Infotech INC" → "photon infotech"
  const normalizeClientName = (name) => {
    return (name || "")
      .replace(/\s*\(.*\)$/g, "")          // Remove trailing parenthetical: "(PHOTON INFOT)"
      .replace(/[,.'"]+/g, " ")             // Replace punctuation with space
      .replace(/\b(inc|llc|ltd|corp|co|incorporated|limited)\b/gi, "") // Remove legal suffixes
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");                // Collapse multiple spaces
  };

  const normalizeInvoice = (inv) => {
    let currentBalance = 0;
    if (inv?.amounts?.due !== undefined && inv?.amounts?.due !== null) {
      currentBalance = toNumber(inv.amounts.due);
    } else if (inv?.amounts?.balance !== undefined && inv?.amounts?.balance !== null) {
      currentBalance = toNumber(inv.amounts.balance);
    }

    // Debug log to inspect raw amounts and status
    if (inv?.invoiceNum === 26040600 || inv?.amounts?.due === 0) {
       console.log(`[DEBUG-AMOUNTS] Inv: ${inv.invoiceNum}`, {
         amounts: inv.amounts,
         status: inv.status,
         balanceCalculated: currentBalance
       });
    }

    return {
      uiid: inv.uiid,
      invoiceNum: inv.invoiceNum,
      invoiceDate: inv.invoiceDate,
      dueDate: inv.dueDate || null,
      balance: currentBalance,
      invoicedAmt: toNumber(inv.amounts?.invoiced?.amt || inv.amounts?.invoiced),
      clientName: inv.clientId?.businessName || "-",
      clientId: inv.clientId?.uiid || inv.clientId?._id || inv.clientId,
      isFunded: inv?.jobId?.invoices?.isFunded === true,
      jobId: inv.jobId?.uiid || inv.jobId?._id || inv.jobId || null,
      netTerms: extractNetTerms(inv),
    };
  };

  const groupByClient = (invoices) => {
    return invoices.reduce((acc, inv) => {
      const cname = (inv.clientName || "unknown").trim();
      const groupKey = normalizeClientName(cname);
      
      if (!acc[groupKey]) {
        acc[groupKey] = {
          clientName: cname,
          clientId: inv.clientId,
          invoices: []
        };
      }
      // Prefer DB name (non-PDF) over PDF name for display
      if (!inv.uiid?.startsWith?.("pdf-") && acc[groupKey].clientName !== cname) {
        acc[groupKey].clientName = cname;
      }
      // Swap out the mock string ID for a real UIID if found later
      const currId = acc[groupKey].clientId;
      if (typeof currId === 'string' && currId === inv.clientName && inv.clientId && inv.clientId !== inv.clientName) {
         acc[groupKey].clientId = inv.clientId;
      }
      acc[groupKey].invoices.push(inv);
      return acc;
    }, {});
  };

  const computeInvoiceBucket = (inv) => {
    const today = dayjs().startOf("day");
    const balance = toNumber(inv.balance !== undefined ? inv.balance : inv.amounts?.due);

    if (!inv.invoiceDate || balance <= 0) return "current";
    
    const dueDate = dayjs(inv.invoiceDate).add(inv.netTerms || 30, "day").startOf("day");
    
    const diff = today.diff(dueDate, "day");

    if (diff <= 0) return "current";
    if (diff <= 30) return "1-30";
    if (diff <= 60) return "31-60";
    if (diff <= 90) return "61-90";
    return "91+";
  };

  const calculateBuckets = (invoices) => {
    let buckets = {
      current: 0,
      d1_30: 0,
      d31_60: 0,
      d61_90: 0,
      d91_plus: 0
    };
    invoices.forEach(inv => {
      // Use due balance to determine if paid/skipped
      const balance = toNumber(inv.balance || 0);
      if (balance <= 0) return;

      // Use total invoiced amount for the actual table row sum instead of opening balance
      const amt = toNumber(inv.invoicedAmt || 0);
      
      const slab = computeInvoiceBucket(inv);
      if (slab === "current") buckets.current += amt;
      else if (slab === "1-30") buckets.d1_30 += amt;
      else if (slab === "31-60") buckets.d31_60 += amt;
      else if (slab === "61-90") buckets.d61_90 += amt;
      else if (slab === "91+") buckets.d91_plus += amt;
    });
    return buckets;
  };

  const fetchAgingData = async () => {
    setLoading(true);
    try {
      const [data, fundingReportResp] = await Promise.all([
        fetchInvoicesAgingReport(normalizedFrom, normalizedTo, 10000, 1),
        fetchAgingReport(normalizedFrom, normalizedTo)
      ]);

      const fundingReportDoc = fundingReportResp?.data || fundingReportResp?.data?.[0];
      const fundingReportList = fundingReportDoc?.report || [];
      
      // Update "As Of Date" using the timestamp of when the report was uploaded
      if (fundingReportDoc?.addedOn) {
        setReportAsOfDate(dayjs(fundingReportDoc.addedOn).toDate());
      } else {
        setReportAsOfDate(dayjs().toDate()); // fallback
      }
      const pdfInvoices = [];
      fundingReportList.forEach(f => {
        (f.invoices || []).forEach(inv => {
          let pushInvoice = true;
          if (normalizedFrom && normalizedTo && inv.invoiceDate) {
              const invDay = dayjs(inv.invoiceDate).startOf("day");
              if (invDay.isBefore(normalizedFrom) || invDay.isAfter(normalizedTo)) {
                  pushInvoice = false;
              }
          }
          if (pushInvoice) {
            pdfInvoices.push({
              ...inv,
              clientName: f.client || "-"
            });
          }
        });
      });

      const normalizedDBInvoices = (data?.invoices || []).map(normalizeInvoice);
      // Build client→netTerms and client→canonical name lookups from DB invoices
      const clientNetTermsMap = {};
      const clientCanonicalNameMap = {};
      normalizedDBInvoices.forEach(inv => {
        const normKey = normalizeClientName(inv.clientName);
        if (normKey) {
          // Store canonical DB name for PDF→DB name resolution
          if (!clientCanonicalNameMap[normKey]) {
            clientCanonicalNameMap[normKey] = (inv.clientName || "").trim();
          }
          if (inv.netTerms && inv.netTerms !== 30) {
            clientNetTermsMap[normKey] = inv.netTerms;
          } else if (!clientNetTermsMap[normKey]) {
            clientNetTermsMap[normKey] = inv.netTerms || 30;
          }
        }
      });

      const normalizedPDFInvoices = pdfInvoices.map(inv => {
          const pdfMatch = String(inv?.netTerms || "").match(/\d+/);
          const pdfNetTerms = pdfMatch ? parseInt(pdfMatch[0], 10) : null;
          const normKey = normalizeClientName(inv.clientName);
          // Look up netTerms and canonical name using fuzzy-normalized key
          const clientLookup = clientNetTermsMap[normKey];
          const canonicalName = clientCanonicalNameMap[normKey];
          const netTerms = pdfNetTerms || clientLookup || 30;

          let currentBalance = 0;
          if (inv?.amounts?.due !== undefined && inv?.amounts?.due !== null) {
            currentBalance = toNumber(inv.amounts.due);
          } else if (inv?.amounts?.balance !== undefined && inv?.amounts?.balance !== null) {
            currentBalance = toNumber(inv.amounts.balance);
          }

          return {
            uiid: `pdf-inv-${inv.invoiceNum}`,
            invoiceNum: inv.invoiceNum,
            invoiceDate: inv.invoiceDate,
            balance: currentBalance,
            invoicedAmt: toNumber(inv.amounts?.invoiced?.amt || inv.amounts?.invoiced),
            // Use DB canonical name if matched, otherwise keep PDF name
            clientName: canonicalName || inv.clientName || "-",
            clientId: inv.clientName || "pdf-client",
            isFunded: true,
            netTerms: netTerms
          }
      });
      // Merge and deduplicate DB and PDF invoices by invoiceNum + jobId
      const uniqueInvoices = new Map();
      const fundedJobIds = new Set();
      
      // Track native DB funded placements
      normalizedDBInvoices.forEach(inv => {
        const cleanInvNum = String(inv.invoiceNum || "-").trim();
        const key = `${cleanInvNum}-${inv.jobId || 'no-job'}`;
        uniqueInvoices.set(key, inv);
        
        if (inv.isFunded && inv.jobId) fundedJobIds.add(inv.jobId);
      });
      
      // Merge PDF Data
      normalizedPDFInvoices.forEach(inv => {
         const cleanInvNum = String(inv.invoiceNum || "-").trim();
         let matched = false;

         // For PDF invoices lacking jobId, we scan for matching invoiceNum
         for (let [key, existing] of uniqueInvoices.entries()) {
            if (String(existing.invoiceNum || "-").trim() === cleanInvNum) {
               existing.isFunded = true;
               if (existing.jobId) fundedJobIds.add(existing.jobId);
               matched = true;
            }
         }

         if (!matched) {
             uniqueInvoices.set(`${cleanInvNum}-pdf-only-${Math.random()}`, inv);
         }
      });
      
      const allInvoices = Array.from(uniqueInvoices.values());
      // Guarantee Placement-level funding uniformity
      allInvoices.forEach(inv => {
         if (inv.jobId && fundedJobIds.has(inv.jobId)) {
             inv.isFunded = true;
         }
      });

      // Exclude all fully paid invoices
      const openInvoices = allInvoices.filter(inv => inv.balance > 0);

      const groupedClients = groupByClient(openInvoices);
      
      const newCachedInvoices = {};
      const mergedRows = [];
      Object.values(groupedClients).forEach((client, i) => {
          const fundedInvoices = client.invoices.filter(inv => inv.isFunded);
          const nonFundedInvoices = client.invoices.filter(inv => !inv.isFunded);
          
          const safeCname = (client.clientName || `client-${i}`).replace(/\s+/g, '').toLowerCase();

          const processRows = (invoicesSubset, statusStr) => {
              if (invoicesSubset.length === 0) return;
              
              const b = calculateBuckets(invoicesSubset);
              const idPrefix = statusStr === "Funded" ? "funded" : "nonfunded";
              const rowId = `${safeCname}-${idPrefix}`;
              const dedupedInvoices = [];
              const seen = new Set();
              invoicesSubset.forEach(inv => {
                  const cleanInvNum = String(inv.invoiceNum || "-").trim();
                  const key = `${cleanInvNum}-${inv.jobId || "pdf"}`;
                  if (!seen.has(key)) {
                     seen.add(key);
                     dedupedInvoices.push(inv);
                  }
              });
              newCachedInvoices[rowId] = dedupedInvoices.map((inv, idx) => ({
                uiid: inv.uiid || `inv-${rowId}-${idx}`,
                invoiceNum: inv.invoiceNum ? String(inv.invoiceNum).trim() : "-",
                invoiceDate: inv.invoiceDate ? dayjs(inv.invoiceDate).toDate() : null,
                dueDate: inv.invoiceDate ? dayjs(inv.invoiceDate).add(inv.netTerms || 30, 'day').toDate() : null,
                netTerms: inv.netTerms || 30,
                amounts: {
                  invoiced: inv.invoicedAmt,
                  due: inv.balance,
                },
                clientFullName: client.clientName || "-",
                clientId: client.clientId,
                isFunded: inv.isFunded,
              }));

              mergedRows.push({
                id: rowId,
                "Client Name": client.clientName,
                Current: (b.current !== 0) ? "$" + formatNumber(b.current) : "-", 
                "1-30": (b.d1_30 !== 0) ? "$" + formatNumber(b.d1_30) : "-",
                "30-60": (b.d31_60 !== 0) ? "$" + formatNumber(b.d31_60) : "-",
                "60-90": (b.d61_90 !== 0) ? "$" + formatNumber(b.d61_90) : "-",
                "90+": (b.d91_plus !== 0) ? "$" + formatNumber(b.d91_plus) : "-",
                fundingStatus: statusStr,
              });
          };

          processRows(fundedInvoices, "Funded");
          processRows(nonFundedInvoices, "Non Funded");
      });
      let filteredRows = mergedRows;
      if (fundingFilter === "Funded") {
        filteredRows = mergedRows.filter(r => r.fundingStatus === "Funded");
      } else if (fundingFilter === "Non-Funded") {
        filteredRows = mergedRows.filter(r => r.fundingStatus === "Non Funded");
      }

      const startIndex = (page - 1) * limit;
      const paginatedRows = filteredRows.slice(startIndex, startIndex + limit);
      setCachedInvoicesMap(newCachedInvoices);
      setAgingData(paginatedRows);
      setTotalCount(filteredRows.length);

    } catch (error) {
      console.error("Error fetching aging report:", error);
      showToast(`${error?.message || "Something went wrong!"} `, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgingData();
  }, [selectedDateRange.from, selectedDateRange.to, page, limit, fundingFilter]);

  const rows = agingData;
  const columns = [
    {
      key: "Client Name",
      labelKey: "Client Name",
      label: "Company Name",
      render: (row) => {
        return (
          <div
            className="text-left flex gap-1 items-center text-nowrap cursor-pointer"
            onClick={() => onClickShowDetails(row.id, row)}
          >
            {row["Client Name"] ? row["Client Name"] : "-"}
          </div>
        );
      },
    },
    {
      key: "fundingStatus",
      labelKey: "fundingStatus",
      label: "Funding Status",
      render: (row) => {
        const isFunded = row.fundingStatus === "Funded";
        return (
          <div className="flex items-center justify-start text-nowrap">
            <span
              className={`text-center text-[10px] p-1 w-[85px] rounded-full ${
                isFunded 
                  ? "bg-info-100/50 text-info-800" 
                  : "bg-danger-100/50 text-danger-800"
              }`}
            >
              {row.fundingStatus}
            </span>
          </div>
        );
      },
    },
    {
      key: "Current",
      labelKey: "Current",
      label: "Current",
      render: (row) => <div className="text-left flex gap-1 items-center text-nowrap cursor-pointer hover:text-linkblue hover:underline"
            onClick={() => onClickShowDetails(row.id, row, "current")}>{row["Current"] || "-"}</div>,
      valueGetter: (row) => toNumber(row["Current"]),
      total: true,
    },
    {
      key: "1-30",
      labelKey: "1-30",
      label: "1-30 Days",
      render: (row) => <div className="text-left flex gap-1 items-center text-nowrap cursor-pointer hover:text-linkblue hover:underline" onClick={() => onClickShowDetails(row.id, row, "1-30")}>{row["1-30"] || "-"}</div>,
      valueGetter: (row) => toNumber(row["1-30"]),
      total: true,
    },
    {
      key: "30-60",
      labelKey: "30-60",
      label: "31-60 Days",
      render: (row) => <div className="text-left flex gap-1 items-center text-nowrap cursor-pointer hover:text-linkblue hover:underline" onClick={() => onClickShowDetails(row.id, row, "31-60")}>{row["30-60"] || "-"}</div>,
      valueGetter: (row) => toNumber(row["30-60"]),
      total: true,
    },
    {
      key: "60-90",
      labelKey: "60-90",
      label: "61-90 Days",
      render: (row) => <div className="text-left flex gap-1 items-center text-nowrap cursor-pointer hover:text-linkblue hover:underline" onClick={() => onClickShowDetails(row.id, row, "61-90")}>{row["60-90"] || "-"}</div>,
      valueGetter: (row) => toNumber(row["60-90"]),
      total: true,
    },
    {
      key: "90+",
      labelKey: "90+",
      label: "91+ Days",
      render: (row) => <div className="text-left flex gap-1 items-center text-nowrap cursor-pointer hover:text-linkblue hover:underline" onClick={() => onClickShowDetails(row.id, row, "91+")}>{row["90+"] || "-"}</div>,
      valueGetter: (row) => toNumber(row["90+"]),
      total: true,
    },
    {
      key: "total",
      labelKey: "total",
      label: "Total",
      render: (row) => {
        return (
          <div
            className="text-left flex gap-1 items-center text-nowrap cursor-pointer hover:text-linkblue hover:underline"
            onClick={() => onClickShowDetails(row.id, row)}
          >
            {"$" + formatNumber(
              toNumber(row["Current"]) +
              toNumber(row["1-30"]) +
              toNumber(row["30-60"]) +
              toNumber(row["60-90"]) +
              toNumber(row["90+"]),
            )}
          </div>
        );
      },
      valueGetter: (row) =>
        toNumber(row["Current"]) +
        toNumber(row["1-30"]) +
        toNumber(row["30-60"]) +
        toNumber(row["60-90"]) +
        toNumber(row["90+"]),
      total: true,
    },
  ];
  const mappedRows = mapTableRows(rows, columns);

  const columnsShowDetails = [
    {
      key: "invoiceDate",
      labelKey: "invoiceDate",
      label: "Date",
      render: (row) => <div>{row.invoiceDate ? dayjs(row.invoiceDate).format("MM/DD/YYYY") : "-"}</div>,
    },
    {
      key: "transactionType",
      labelKey: "transactionType",
      label: "Transaction Type",
      render: () => <div>Invoiced</div>,
    },
    {
      key: "invoiceNum",
      labelKey: "invoiceNum",
      label: "Invoice Number",
      render: (row) => (
        <div
          className="flex items-center gap-1 cursor-pointer text-linkblue"
          onClick={() => handleClickForInvoice(row.invoiceNum, row.uiid)}
        >
          <span className="cursor-pointer text-linkblue line-clamp-1 hover:underline">
            {row.invoiceNum || "-"}
          </span>
        </div>
      ),
    },
    {
      key: "clientFullName",
      labelKey: "clientFullName",
      label: "Client Full Name",
      render: (row) => <div>{row.clientFullName || "-"}</div>,
    },
    // {
    //   key: "locationFullName",
    //   labelKey: "locationFullName",
    //   label: "Location Full Name",
    //   render: (row) => {
    //     let address = "";
    //     if (row?.address?.billing) {
    //       const { address1, city, state, zipCode } = row.address.billing;
    //       address = [address1, city, state, zipCode].filter(Boolean).join(", ");
    //     } else if (row?.clientId?.generalInfo?.locations && row.clientId.generalInfo.locations.length > 0) {
    //       const loc = row.clientId.generalInfo.locations[0];
    //       address = [loc.address1, loc.city, loc.state, loc.zipCode].filter(Boolean).join(", ");
    //     }
    //     return (
    //       <div className="max-w-[250px] truncate" title={address || "-"}>
    //         {address || "-"}
    //       </div>
    //     );
    //   },
    // },
    {
      key: "dueDate",
      labelKey: "dueDate",
      label: "Due Date",
      render: (row) => <div>{row.dueDate ? dayjs(row.dueDate).format("MM/DD/YYYY") : "-"}</div>,
    },
    {
      key: "amount",
      labelKey: "amount",
      label: "Amount",
      render: (row) => <div>{"$" + formatNumber(row.amounts?.invoiced || 0)}</div>,
      valueGetter: (row) => toNumber(row.amounts?.invoiced || 0),
      total: true,
      totalRender: (val) => <div className="text-left">{"$" + formatNumber(val || 0)}</div>,
    },
    {
      key: "openingBalance",
      labelKey: "openingBalance",
      label: "Opening Balance",
      render: (row) => <div>{"$" + formatNumber(row.amounts?.due || 0)}</div>,
      valueGetter: (row) => toNumber(row.amounts?.due || 0),
      total: true,
      totalRender: (val) => <div className="text-left">{"$" + formatNumber(val || 0)}</div>,
    },
  ];
  const getInvoiceAgingBucket = (row) => {
    return computeInvoiceBucket(row);
  };
  const filteredClientInvoices = useMemo(() => {
    if (selectedAgingBucket === "all") return clientInvoices;
    return clientInvoices.filter(
      (row) => getInvoiceAgingBucket(row) === selectedAgingBucket
    );
  }, [clientInvoices, selectedAgingBucket]);

  const mappedRowsShowDetails = mapTableRows(
    filteredClientInvoices,
    columnsShowDetails,
  );

  return (
    <div className="flex-1 flex flex-col p-2 overflow-hidden">
      <div className="flex justify-between items-center w-full">
        <div className="flex gap-1 items-baseline">
        {showDetails && (
          <Button
            variant="text"
            color="dark"
            size="sm"
            type="icon"
            icon={"west"}
            onClick={closeShowDetails}
          />
        )}
        <PageTitle title="A/R Aging Summary Report" />
        <span className="text-xs text-gray-300">
          (As of {reportAsOfDate 
            ? dayjs(reportAsOfDate).format("MMMM DD, YYYY") 
            : (selectedDateRange?.to ? dayjs(selectedDateRange.to).format("MMMM DD, YYYY") : dayjs().format("MMMM DD, YYYY"))
          })
        </span>
        </div>
        {showDetails && (
          <div className="w-[200px]">
            <FormFieldSet
              noTitle
              type="select"
              options={[
                { label: "All Balances", value: "all" },
                { label: "Current", value: "current" },
                { label: "1-30 Days", value: "1-30" },
                { label: "31-60 Days", value: "31-60" },
                { label: "61-90 Days", value: "61-90" },
                { label: "91+ Days", value: "91+" },
              ]}
              value={selectedAgingBucket}
              onChange={(val) => setSelectedAgingBucket(val)}
              placeholder="Filter Aging..."
            />
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className={`flex-1 flex flex-col overflow-hidden my-3 relative ${showDetails ? "flex" : "hidden"}`}>
            <TableAdv
              columns={columnsShowDetails}
              rows={mappedRowsShowDetails}
              hideToolBar={true}
              totalText={
                <div className="inline-block w-[100px] text-left pr-4">
                  Total
                </div>
              }
            />
          <Backdrop isOpen={detailsLoading} className="absolute inset-0 z-10 bg-white/50">
            <LogoLoader />
          </Backdrop>
        </div>

        <div className={`flex-1 flex flex-col overflow-hidden ${!showDetails ? "flex" : "hidden"}`}>
            <div>
              <div className="flex gap-2 items-baseline my-3">
                <FormFieldSet
                  title="Select date"
                  type="datepickerRange"
                  placeholder="Select date"
                  value={selectedDateRange}
                  onChange={onDateRangeChange}
                />
              </div>
            </div>
            <TableAdv 
              columns={columns} 
              rows={mappedRows} 
              hideToolBar={false} 
              totalCount={totalCount}
              page={page}
              limit={limit}
              serverPagination={true}
              onPaginationChange={setPage}
              onLimitChange={handleLimitChange}
              toolbarLeft={{
                add: [
                  {
                    component: (
                      <div className="w-44 ml-2">
                        <FormFieldSet
                          noTitle
                          type="select"
                          options={[
                            { label: "All", value: "all" },
                            { label: "Funded", value: "Funded" },
                            { label: "Non-Funded", value: "Non-Funded" },
                          ]}
                          value={fundingFilter}
                          onChange={onFundingFilterChange}
                          placeholder="Select Funding Status"
                          size="sm"
                        />
                      </div>
                    )
                  }
                ]
              }}
            />
          </div>
        </div>
      <Backdrop isOpen={loading}>
        <LogoLoader />
      </Backdrop>

      <Modal
        isOpen={Boolean(selectedInvoice)}
        title={"Invoice Details"}
        onClose={() => setSelectedInvoice(null)}
        showCloseButton={true}
        closeOnOutsideClick={true}
        width="max-w-full"
        zIndex={100}
      >
        {selectedInvoice && (
          <InvoiceDetails
            invoice={selectedInvoice} 
            onBack={() => setSelectedInvoice(null)}
            removeButtonForPreview={true}
          />
        )}
      </Modal>
    </div>
  );
};

export default Aging;
